import { ChangeDetectionStrategy, Component, forwardRef, OnInit, inject, input } from '@angular/core';
import { ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
import { IdValue } from '../../../shared/models/id-value.model';
import { NgClass } from '@angular/common';
import { RadioButton } from 'primeng/radiobutton';
import { CmgFormControlDomDirective } from '../../directives/cmg-form-control-dom.directive';
import { QaSlugPipe } from '../../pipes/qa-slug.pipe';

/**
 * Component was designed as a workaround for issue found with PrimeNg radio buttons and FormArrays
 * When using FormArray and p-radioButton the radio buttons were using the same formControlName
 * and PrimeNg doesn't differentiate between the two FormArrays. So when you click on one radio button
 * it will change the rest of the radio buttons with the same formControlName
 * https://github.com/primefaces/primeng/issues/9723
 * @Input() Options takes an array of IdValue
 * IdValue property `name` is used for the label of radio button
 * IdValue property `value` is used for the value of radio button
 */
@Component({
  selector: 'cmg-form-array-radio-button[options]',
  templateUrl: './cmg-form-array-radio-button.component.html',
  styleUrls: ['./cmg-form-array-radio-button.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CmgFormArrayRadioButtonComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, NgClass, RadioButton, CmgFormControlDomDirective, QaSlugPipe],
})
export class CmgFormArrayRadioButtonComponent implements OnInit, ControlValueAccessor {
  private readonly _fb = inject(UntypedFormBuilder);

  readonly options = input<IdValue[]>(undefined);
  readonly automationId = input<string>(undefined);
  readonly radioGroupName = input('radioGroup');
  form: UntypedFormGroup;

  ngOnInit(): void {
    this.form = this._fb.group({
      [this.radioGroupName()]: [false],
    });
    this.radioGroup.valueChanges.pipe(distinctUntilChanged()).subscribe((radioValue) => {
      this.value = radioValue;
    });
  }

  get radioGroup(): UntypedFormGroup {
    return this.form.get(this.radioGroupName()) as UntypedFormGroup;
  }

  registerOnChange(fn) {
    this._onChange = fn;
  }

  registerOnTouched(fn) {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  get value(): any {
    return this.radioGroup.value;
  }

  set value(value: any) {
    this.radioGroup.setValue(value);
    this._onChange(value);
    this._onTouched();
  }

  private _onChange: any = () => {
    //
  };

  private _onTouched: any = () => {
    //
  };

  writeValue(value) {
    this.value = value;

    if (value === null) {
      this.form.reset();
    }
  }
}
